import { FormatNumber, HStack, Skeleton, Text } from '@chakra-ui/react';
import { ChatBubble, Eye, Heart, ShareAndroid } from 'iconoir-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMentionCardStats } from './mention-stats-context';
import { MentionPlatform, type MentionStats } from '@fluenceur/types';

interface MentionCardStatsProps {
  mentionId: string;
  platform?: MentionPlatform;
  /** Denser sizing for the compact card variant. */
  compact?: boolean;
}

type CounterKey = 'views' | 'shares' | 'likes' | 'comments';

// TikTok cards surface the platform's signature counters (views + shares);
// Instagram keeps likes + comments. Order mirrors the preview header, where
// the full counter set lives.
const counterDefs = (
  platform: MentionPlatform,
  stats?: MentionStats
): { key: CounterKey; Icon: typeof Heart; value: number | null | undefined }[] =>
  platform === MentionPlatform.TIKTOK
    ? [
        { key: 'views', Icon: Eye, value: stats?.view_count },
        { key: 'shares', Icon: ShareAndroid, value: stats?.share_count },
      ]
    : [
        { key: 'likes', Icon: Heart, value: stats?.like_count },
        { key: 'comments', Icon: ChatBubble, value: stats?.comments_count },
      ];

/**
 * Compact platform-aware stats display for mention cards. Stats are resolved
 * once by the grid's MentionStatsProvider in a single batched request; the card
 * reads its own slice from context instead of fetching per card.
 */
export const MentionCardStats: React.FC<MentionCardStatsProps> = ({
  mentionId,
  platform = MentionPlatform.INSTAGRAM,
  compact = false,
}) => {
  const { t } = useTranslation();
  const { stats, isLoading } = useMentionCardStats(mentionId);

  const iconSize = compact ? 14 : 16;

  return (
    <HStack gap={compact ? 2 : 2.5} flexShrink={0} ml="auto">
      {counterDefs(platform, stats).map(({ key, Icon, value }) => (
        <HStack key={key} gap={1} aria-label={t(`mentions.details.${key}`)}>
          <Icon
            width={iconSize}
            height={iconSize}
            color="var(--chakra-colors-primary-400)"
            strokeWidth={2}
            aria-hidden
          />
          <Skeleton height="16px" loading={isLoading}>
            <Text
              fontFamily="heading"
              fontWeight="medium"
              fontSize={compact ? '11px' : '11.5px'}
              color="primary.500"
              whiteSpace="nowrap"
            >
              {value != null ? (
                <FormatNumber value={value} notation="compact" compactDisplay="short" />
              ) : (
                '--'
              )}
            </Text>
          </Skeleton>
        </HStack>
      ))}
    </HStack>
  );
};
